import { SelectionModel } from '@angular/cdk/collections';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  linkedSignal,
  signal,
  TrackByFunction,
} from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import {
  MatDrawer,
  MatDrawerContainer,
  MatDrawerContent,
} from '@angular/material/sidenav';

import { MatTreeModule } from '@angular/material/tree';

import { NgClass, NgTemplateOutlet } from '@angular/common';
import { HttpEventType } from '@angular/common/http';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ValidationErrors, Validators } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { clamp, head, isNil } from 'lodash-es';
import {
  catchError,
  combineLatest,
  EMPTY,
  filter,
  finalize,
  from,
  map,
  mergeMap,
  Observable,
  of,
  startWith,
  Subject,
  switchMap,
  tap,
  toArray,
  withLatestFrom,
} from 'rxjs';
import {
  FileService,
  HasPermissionDirective,
  isHttpErrorResponse,
  PermissionService,
  triggerIfSlow,
} from '@ipg/ngx-frontend-toolset';
import { BreadcrumbDefDirective } from '../../components/breadcrumbs/breadcrumb-def.directive';
import { BreadcrumbsComponent } from '../../components/breadcrumbs/breadcrumbs.component';
import { Breadcrumb } from '../../components/breadcrumbs/breadcrumbs.model';
import {
  MediaFileCardComponent,
  MediaSelectionChange,
} from '../../components/cards/media-file-card/media-file-card.component';
import { ConfirmHandler } from '../../components/dialog/dialog.model';
import { DialogService } from '../../components/dialog/dialog.service';
import { HeaderComponent } from '../../components/header/header.component';
import { MediaPickerDialogService } from '../../components/media-picker-dialog/services/media-picker-dialog.service';
import { MoveToFolderDialogService } from '../../components/move-to-folder-dialog/services/move-to-folder-dialog.service';
import { EmptyStatePlaceholderComponent } from '../../components/placeholders/empty-state-placeholder/empty-state-placeholder.component';

import { GlobalLoadersService } from '../../global-loaders.service';
import { TreeNode } from '../../models/common/tree-node.model';
import { StatusResult } from '../../models/common/utils';
import { Group } from '../../models/group.model';
import { MediaContext } from '../../models/media-context.model';
import {
  MediaCreateResponse,
  MediaItem,
  MediaReference,
  MediaResponse,
  SharedMediaItem,
  SharedMediaResponse,
} from '../../models/media.model';
import { GroupsService } from '../../services/groups.service';
import { ImageCompressionService } from '../../services/image-compression/image-compression.service';
import { MediaService } from '../../services/media.service';

import { ShareService } from '../../services/share.service';
import { buildTreeFrom } from '../../utils/tree-node';
import { escapeHtml } from '../../utils/html';
import { UnitService } from '../units/services/unit.service';
import {
  MediaReferencesDialogComponent,
  ReferenceGroup,
} from './components/media-references-dialog/media-references-dialog.component';
import { MediaSettingsDialogComponent } from './components/media-settings-dialog/media-settings-dialog.component';
import {
  isAttachedResourcesResponse,
  MediaAttachedResources,
} from './models/error-metadata-response.model';
import { UploadFile } from './models/file-upload.model';
import { FileUploadDialogService } from './services/file-upload-dialog.service';
import { FileUploadService } from './services/file-upload.service';

export const VIRTUAL_ROOT_NODE_ID = '__home__';

export const VIRTUAL_ROOT_NODE: Group = {
  _id: VIRTUAL_ROOT_NODE_ID,
  label: 'Home',
  parentId: null,
  createdAt: '',
  updatedAt: '',
  unitId: '',
};

enum FetchState {
  MEDIA = 'media',
  GROUP = 'group',
  ALL = 'all',
}

@Component({
  selector: 'app-media',
  imports: [
    MatButton,
    MatIcon,
    MatIconButton,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    MatDrawer,
    MatDrawerContainer,
    MatDrawerContent,
    MatTreeModule,
    MatTooltip,
    BreadcrumbsComponent,
    BreadcrumbDefDirective,
    MediaFileCardComponent,
    EmptyStatePlaceholderComponent,
    NgClass,
    NgTemplateOutlet,
    HasPermissionDirective,
  ],
  templateUrl: './media.component.html',
  styleUrl: './media.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MediaComponent {
  private static readonly LOADER_CALLER_ID = 'media_loader_caller';
  private readonly _dialog = inject(DialogService);
  private readonly _groups = inject(GroupsService);
  private readonly _file = inject(FileService);
  private readonly _fileUpload = inject(FileUploadService);
  private readonly _fileUploadDialog = inject(FileUploadDialogService);
  private readonly _imageCompression = inject(ImageCompressionService);
  private readonly _moveToFolderDialog = inject(MoveToFolderDialogService);
  private readonly _media = inject(MediaService);
  private readonly _mediaPicker = inject(MediaPickerDialogService);
  private readonly _share = inject(ShareService);
  private readonly _router = inject(Router);
  private readonly _route = inject(ActivatedRoute);
  private readonly _unitService = inject(UnitService);
  private readonly _permissionService = inject(PermissionService);

  /** Media context: 'unit' (default) or 'client' (when nested under /clients/:clientId). */
  protected readonly mediaContext = computed<MediaContext>(() => {
    const scope = this._route.snapshot.data['scope'] as string | undefined;
    if (scope === 'client') {
      const clientId = this._route.parent?.snapshot.params['clientId'];
      if (clientId) {
        return { scope: 'client', clientId };
      }
    }
    return { scope: 'unit' };
  });

  protected readonly isClientScope = computed(
    () => this.mediaContext().scope === 'client'
  );

  protected readonly canEditMedia = computed(() =>
    this._permissionService.hasAnyPermission(['media:create', 'media:delete'])
  );

  protected readonly canViewSharedMedia = computed(
    () => !this.isClientScope() && this._permissionService.hasPermission('media:share')
  );

  /*
    Folder actions ask for different permissions per library, after the
    endpoints behind them. A unit's folders are groups: `group:read` lists
    them (so every other folder action needs it too), `group:create` and
    `group:update` change them. A client's folders belong to its media, which
    the page already reads: `media:create` changes them. Deleting a folder
    deletes its media and takes `media:delete` plus `group:delete` in both.
  */
  protected readonly canListFolders = computed(
    () => this.isClientScope() || this._permissionService.hasPermission('group:read')
  );
  protected readonly canCreateFolder = computed(
    () =>
      this.canListFolders() &&
      this._permissionService.hasPermission(
        this.isClientScope() ? 'media:create' : 'group:create'
      )
  );
  protected readonly canEditFolder = computed(
    () =>
      this.canListFolders() &&
      this._permissionService.hasPermission(
        this.isClientScope() ? 'media:create' : 'group:update'
      )
  );
  protected readonly canDeleteFolder = computed(
    () =>
      this.canListFolders() &&
      this._permissionService.hasAllPermissions(['media:delete', 'group:delete'])
  );
  /** Moving files picks the target from the folder list */
  protected readonly canMoveFiles = computed(
    () => this.canListFolders() && this._permissionService.hasPermission('media:create')
  );

  private readonly _fetch$ = new Subject<FetchState>();
  private readonly _firstRequestFired = signal(false);

  private readonly _currentGroupId$ = this._route.queryParamMap.pipe(
    map((params) => params.get('groupId'))
  );

  private readonly _currentView$ = this._route.queryParamMap.pipe(
    map((params) => params.get('view'))
  );

  private readonly currentUnit = this._unitService.currentUnit;

  protected readonly VIRTUAL_ROOT_NODE_ID = VIRTUAL_ROOT_NODE_ID;

  protected readonly currentGroupId = toSignal(this._currentGroupId$);

  protected readonly currentView = toSignal(this._currentView$);

  protected readonly isSharedView = computed(() => this.currentView() === 'shared');

  protected isOwnGroup(group: Group): boolean {
    return group.unitId === this.currentUnit()?._id;
  }

  protected canEditSelection(): boolean {
    return this.selection.selected.every((item) => !('isOwn' in item) || item.isOwn);
  }

  protected isSelectionShared(): boolean {
    return this.selection.selected.some((item) => !!item.shareId);
  }

  protected readonly groupList = signal<Group[]>([]);

  protected readonly sharedGroupList = signal<Group[]>([]);

  /** All shared groups for the sidebar tree — never overwritten by navigation. */
  private readonly _sidebarSharedGroups = signal<Group[]>([]);

  protected readonly sharedTreeNodes = computed(() =>
    buildTreeFrom(this._sidebarSharedGroups(), this.mapToTreeNode, (a, b) =>
      a.name.localeCompare(b.name)
    )
  );

  protected readonly sharedExpandedIds = signal<Set<string>>(new Set());

  private readonly _sharedGroupIds = computed(
    () => new Set(this.sharedGroupList().map((g) => g._id))
  );

  protected isFolderShared(group: Group): boolean {
    return !!group.shareId;
  }

  protected readonly isSharedExpanded = signal(false);

  protected readonly groups = computed(() => {
    const list = this.isSharedView() ? this.sharedGroupList() : this.groupList();
    return list
      .sort((a, b) => a.label.localeCompare(b.label))
      .filter((value) => value.parentId === this.currentGroupId());
  });

  protected readonly treeNodes = computed(() => this.buildTree(this.groupList()));

  protected readonly selectedFolderId = linkedSignal({
    source: this.currentGroupId,
    computation: (source) => source ?? VIRTUAL_ROOT_NODE_ID,
  });

  private readonly _media$ = combineLatest({
    groupId: this._currentGroupId$,
    view: this._currentView$,
    __fetch: this._fetch$.pipe(
      startWith(FetchState.MEDIA),
      filter((state) => state === FetchState.MEDIA || state === FetchState.ALL)
    ),
  }).pipe(
    tap(() => this.dataSource.set([])),
    switchMap(({ groupId, view }) => {
      const isShared = view === 'shared';

      if (isShared && !this._permissionService.hasPermission('media:share')) {
        return of<MediaItem[]>([]);
      }

      const fetch$ = isShared
        ? this._share
            .getSharedMedia({ params: { groupId: groupId ?? '' } })
            .pipe(
              map((response) =>
                response.map((value) => this.mapToSharedMediaItem(value))
              )
            )
        : this._media
            .getAll({ params: { parentId: groupId ?? '' } }, this.mediaContext())
            .pipe(
              map((response) => response.map((value) => this.mapToMediaItem(value)))
            );

      return fetch$.pipe(
        tap({
          error: () =>
            this._dialog.alert({
              title: `Foutmelding`,
              message: `Er is iets misgegaan bij het ophalen van de afbeeldingen. Probeer het later nog eens`,
              confirmText: `OK`,
            }),
        }),
        takeUntilDestroyed(this._destroyRef),
        triggerIfSlow(() =>
          this._globalLoaders.setActive(
            MediaComponent.LOADER_CALLER_ID,
            HeaderComponent.LOADER_ID
          )
        ),
        map((items) => [...items].sort((a, b) => a.label.localeCompare(b.label))),
        catchError(() => of([])),
        finalize(() =>
          this._globalLoaders.setInactive(
            MediaComponent.LOADER_CALLER_ID,
            HeaderComponent.LOADER_ID
          )
        )
      );
    }),
    tap(() => this._firstRequestFired.set(true))
  );

  private readonly _destroyRef = inject(DestroyRef);

  private readonly _globalLoaders = inject(GlobalLoadersService);

  protected readonly dataSource = signal<MediaItem[]>([]);
  protected readonly showPlaceholder = computed(() => {
    return (
      this._firstRequestFired() && !this.groups().length && !this.dataSource().length
    );
  });

  protected readonly breadcrumbs = computed(() => {
    const crumbs = this.buildBreadcrumbs(this.selectedFolderId(), this.groupList());

    if (this.isSharedView() && crumbs.length > 0) {
      crumbs[0] = { ...crumbs[0], name: 'Gedeelde media' };
    }

    return crumbs;
  });

  protected readonly pinnedFolderIds = signal<Set<string>>(new Set());

  protected readonly pinnedFolders = computed(() =>
    this.groupList()
      .filter((group) => this.pinnedFolderIds().has(group._id))
      .sort((a, b) => a.label.localeCompare(b.label))
  );

  protected readonly selection = new SelectionModel<MediaItem>(true, []);

  private readonly _drawerSizeConfig = {
    min: 256,
    max: 540,
  };

  protected readonly isResizing = signal(false);
  protected readonly drawerSize = signal<number>(this._drawerSizeConfig.min);
  protected readonly sidebarCollapsed = signal<boolean>(this._loadSidebarCollapsed());
  protected lastSelectedIndex = -1;

  constructor() {
    this._media$.subscribe((mediaData) => {
      this.dataSource.set(mediaData);
    });

    this._fetch$
      .pipe(
        startWith(FetchState.GROUP),
        filter((state) => state === FetchState.GROUP || state === FetchState.ALL),
        switchMap(() => {
          if (!this.canListFolders()) {
            this._firstRequestFired.set(true);
            return of<Group[]>([]);
          }

          return this._groups.getList(this.mediaContext()).pipe(
            tap({
              error: () =>
                this._dialog.alert({
                  title: `Foutmelding`,
                  message: `Er is iets misgegaan bij het ophalen van de media folders. Probeer het later nog eens`,
                  confirmText: `OK`,
                }),
            }),
            finalize(() => this._firstRequestFired.set(true))
          );
        })
      )
      .subscribe((result) => {
        this.groupList.set(result);
        this.loadPinnedFolders();
        this.cleanupDeletedPins();
      });

    // Fetch shared groups for sidebar (initial) and content area (reactive on view change)
    // Skip in client scope — shared media is not applicable
    if (!this.isClientScope() && this._permissionService.hasPermission('media:share')) {
      // Fetch full tree of shared groups for the sidebar
      this._share
        .getSharedGroupsTree()
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe((result) => {
          this._sidebarSharedGroups.set(result);
          // Also seed the content-area shared group list with root-level groups
          this.sharedGroupList.set(result.filter((g) => g.parentId === null));
        });

      // Re-fetch shared groups when entering shared view or navigating within it
      combineLatest([this._currentView$, this._currentGroupId$])
        .pipe(
          filter(([view]) => view === 'shared'),
          switchMap(([, groupId]) => {
            const params: Record<string, string> = {};
            if (groupId) params['groupId'] = groupId;
            return this._share.getSharedGroups({ params });
          }),
          takeUntilDestroyed(this._destroyRef)
        )
        .subscribe((result) => this.sharedGroupList.set(result));
    }
  }

  handleMouseMove(event: MouseEvent) {
    if (this.isResizing()) {
      const container = event.currentTarget as HTMLElement;
      const containerLeft = container.getBoundingClientRect().left;

      this.drawerSize.set(
        clamp(
          event.clientX - containerLeft,
          this._drawerSizeConfig.min,
          this._drawerSizeConfig.max
        )
      );
    }
  }

  handleMouseUp() {
    this.isResizing.set(false);
  }

  handleMouseDown() {
    this.isResizing.set(true);
  }

  handleSelectionChange({ source, value }: MediaSelectionChange, index: number) {
    if (!this._isSelectable(value)) {
      return;
    }

    const isCtrlPressed = source.ctrlKey;
    const isShiftPressed = source.shiftKey;
    const isSelected = this.selection.isSelected(value);

    if (isShiftPressed && this.lastSelectedIndex > -1) {
      const minIndex = Math.min(this.lastSelectedIndex, index);
      const maxIndex = Math.max(this.lastSelectedIndex, index);
      const range = this.dataSource()
        .slice(minIndex, maxIndex + 1)
        .filter((item) => this._isSelectable(item));

      this.selection.select(...range);
      this.lastSelectedIndex = index;
    } else if (isCtrlPressed) {
      this.selection.toggle(value);
      this.lastSelectedIndex = index;
    } else {
      if (isSelected && this.selection.selected.length === 1) {
        this.selection.clear();
        this.lastSelectedIndex = -1;

        return;
      }
      this.selection.clear();
      this.selection.select(value);
      this.lastSelectedIndex = index;
    }
  }

  trackByNodeFn: TrackByFunction<TreeNode> = (_, node) => node;

  expansionKeyFn = (node: TreeNode) => node.id;

  childrenAccessor = (node: TreeNode) => node.children;

  navigateFolder(groupId: string, preserveSharedView = true) {
    const isHome = groupId === VIRTUAL_ROOT_NODE_ID;
    const queryParams: Record<string, string> = {};

    if (!isHome) {
      queryParams['groupId'] = groupId;

      // Only preserve shared view when drilling into subfolders, not when clicking Home
      if (preserveSharedView && this.isSharedView()) {
        queryParams['view'] = 'shared';
      }
    }

    this._firstRequestFired.set(false);
    this.selection.clear();

    if (this.isClientScope()) {
      this._router.navigate([], { relativeTo: this._route, queryParams });
    } else {
      this._router.navigate(['media'], { queryParams });
    }
  }

  addFolder() {
    const promptRef = this._handleDialogPrompt(
      (result) =>
        of(result).pipe(
          filter((result) => result != null),
          map((result) => ({ label: result })),
          withLatestFrom(this._currentGroupId$),
          map(([data, parentId]) => ({
            ...data,
            parentId: parentId,
          })),
          switchMap((data) =>
            this._groups.create(data, undefined, this.mediaContext()).pipe(
              catchError((error) => {
                promptRef.close();
                const { title, message } = this._getErrorAlert(
                  error,
                  'Er is iets misgegaan bij het aanmaken van een folder. Probeer het later nog eens',
                  {
                    GROUP_NAME_ALREADY_EXIST: {
                      title: 'Foldernaam al in gebruik',
                      message:
                        'Er bestaat al een folder met deze naam. Kies een andere naam.',
                    },
                  }
                );
                this._dialog.alert({ title, message, confirmText: 'OK' });

                return EMPTY;
              }),
              switchMap((groupId) =>
                this._groups.getById(groupId, undefined, this.mediaContext())
              ),
              tap((group) =>
                this.groupList.update((currentState) => [...currentState, group])
              )
            )
          )
        ),
      { title: 'Nieuwe folder' }
    );
  }

  deleteFolder({ _id, label }: Group) {
    const confirmDialogRef = this._dialog.confirm({
      title: `${label} verwijderen?`,
      message: 'Alle bestanden en subfolders in deze folder worden ook verwijderd',
      confirmText: 'Verwijderen',
      cancelText: 'Annuleren',
      onConfirm: () =>
        this._media.deleteGroup(_id).pipe(
          tap({
            next: () => this.findChildrenAndRemove(_id),
            error: (e) => {
              confirmDialogRef.close();

              if (
                isHttpErrorResponse(e) &&
                isAttachedResourcesResponse(e.error.detail.metadata)
              ) {
                const { metadata } = e.error.detail;
                const referenceGroups = (metadata.attachedResources as any[]).map(
                  (r: any) => ({
                    unitId: r.unitId ?? null,
                    unitLabel: r.unitLabel ?? '',
                    references: (r.references ?? [r]).map((ref: any) => ({
                      ...ref,
                      unitId: ref.unitId ?? null,
                      unitLabel: ref.unitLabel ?? '',
                    })),
                    totalCount: r.totalCount ?? 1,
                  })
                );

                if (referenceGroups.length) {
                  const deletableCount = (metadata as any).deletableCount ?? 0;

                  const dialogRef = this._dialog.open(MediaReferencesDialogComponent, {
                    referenceGroups,
                    mode: 'delete' as const,
                    deletableCount,
                  });

                  dialogRef.afterClosed().subscribe((result) => {
                    if (result?.deleteRemaining) {
                      this._media
                        .deleteGroup(_id, { skip_referenced: true })
                        .pipe(takeUntilDestroyed(this._destroyRef))
                        .subscribe({
                          next: () => this._fetch$.next(FetchState.MEDIA),
                          error: () => {
                            this._dialog.alert({
                              title: 'Foutmelding',
                              message: `Er is iets misgegaan bij het verwijderen van folder ${escapeHtml(label)}. Probeer het later nog eens`,
                              confirmText: 'Begrepen',
                            });
                          },
                        });
                    }
                  });

                  return;
                }
              }

              this._dialog.alert({
                title: 'Foutmelding',
                message: `Er is iets misgegaan bij het verwijderen van folder ${escapeHtml(label)}. Probeer het later nog eens`,
                confirmText: 'Begrepen',
              });
            },
          })
        ),
    });
  }

  moveFolder(node: Group) {
    this._moveToFolderDialog.open(
      (result) =>
        of(result).pipe(
          filter((result) => !isNil(result)),
          switchMap((result) =>
            this._groups.moveTo(
              {
                targetGroupId: result._id === VIRTUAL_ROOT_NODE_ID ? null : result._id,
                sourceGroupIds: [node._id],
              },
              this.mediaContext()
            )
          ),
          tap(() => this._fetch$.next(FetchState.GROUP))
        ),
      {
        title: `${node.label} verplaatsen`,
        currentFolderId: node._id,
        type: 'folder',
        sourceIds: [node._id],
        context: this.mediaContext(),
      }
    );
  }

  moveFiles(items: MediaItem[]) {
    let title: string | undefined;

    if (items.length > 1) {
      title = `${items.length} items verplaatsen`;
    } else {
      const firstItem = head(items);

      if (firstItem) {
        title = `${firstItem.label} verplaatsen`;
      }
    }

    const mediaIds = items.map((value) => value.id);

    this._moveToFolderDialog.open(
      (result) =>
        of(result).pipe(
          filter((result) => !isNil(result)),
          switchMap((result) =>
            this._media.moveTo(
              {
                mediaIds,
                groupId: result._id === VIRTUAL_ROOT_NODE_ID ? null : result._id,
              },
              this.mediaContext()
            )
          ),
          tap(() => {
            this.selection.clear();

            this.dataSource.update((currentState) =>
              currentState.filter(({ id }) => !mediaIds.includes(id))
            );
          })
        ),
      {
        title,
        currentFolderId: this.currentGroupId() ?? VIRTUAL_ROOT_NODE_ID,
        type: 'file',
        context: this.mediaContext(),
      }
    );
  }

  editFolderName(group: Group) {
    const promptRef = this._handleDialogPrompt(
      (result) =>
        of(result).pipe(
          filter((result) => result != null),
          switchMap((result) =>
            this._groups.update(group._id, { label: result }, undefined, this.mediaContext()).pipe(
              catchError((error) => {
                promptRef.close();
                const { title, message } = this._getErrorAlert(
                  error,
                  `Er is iets misgegaan bij het bewerken van folder ${group.label}. Probeer het later nog eens`,
                  {
                    GROUP_NAME_ALREADY_EXIST: {
                      title: 'Foldernaam al in gebruik',
                      message:
                        'Er bestaat al een folder met deze naam. Kies een andere naam.',
                    },
                  }
                );
                this._dialog.alert({ title, message, confirmText: 'OK' });

                return EMPTY;
              }),
              switchMap(() => this._groups.getById(group._id, undefined, this.mediaContext())),
              tap((result) => {
                const updateList = (currentState: Group[]) =>
                  currentState.map((value) => {
                    if (value._id === result._id) {
                      return {
                        ...value,
                        ...result,
                      };
                    }

                    return value;
                  });

                this.groupList.update(updateList);

                if (this.isSharedView()) {
                  this.sharedGroupList.update(updateList);
                  this._sidebarSharedGroups.update(updateList);
                }
              })
            )
          )
        ),
      {
        title: 'Naam wijzigen',
        value: group.label,
      }
    );
  }

  getChildrenFromParentId(parentId: string) {
    const groups = this.groupList();
    const lookup = groups.reduce((map, group): Map<string, Group[]> => {
      if (group.parentId) {
        if (!map.has(group.parentId)) {
          map.set(group.parentId, []);
        }

        map.get(group.parentId)!.push(group);
      }

      return map;
    }, new Map<string, Group[]>());

    const getChildren = (id: string): Group[] => {
      const children = lookup.get(id) || [];

      return children.reduce<Group[]>((acc, node) => {
        return acc.concat(node, ...getChildren(node._id));
      }, []);
    };

    return getChildren(parentId);
  }

  findChildrenAndRemove(parentId: string) {
    const children = this.getChildrenFromParentId(parentId);
    const childIds = children.map((node) => node._id);

    const idsToRemove = new Set([parentId, ...childIds]);

    this.groupList.update((currentState) =>
      currentState.filter((value) => !idsToRemove.has(value._id))
    );
  }

  makeFileUploadRequest(
    file: File,
    {
      fileId,
      mediaId,
      duration,
    }: { fileId: string; mediaId: string; duration?: number }
  ) {
    return this._fileUpload.upload({
      file,
      fileId: fileId,
      references: {
        collection: 'media',
        id: mediaId,
      },
      metadata: {
        ...(duration != undefined ? { duration } : undefined),
      },
    });
  }

  getDurationFromAudio(file: File) {
    return new Promise<number | undefined>((resolve) => {
      const audio = new Audio();
      audio.preload = 'metadata';

      const url = URL.createObjectURL(file);
      audio.src = url;

      audio.onloadedmetadata = () => {
        URL.revokeObjectURL(url);

        resolve(audio.duration);
      };

      audio.onerror = () => {
        URL.revokeObjectURL(url);

        resolve(undefined);
      };
    });
  }

  uploadFiles(files: UploadFile[], concurrent = 5) {
    return from(files).pipe(
      mergeMap((file) => {
        file.setState({ uploading: true });

        return this._media
          .create(
            {
              label: file.file.name,
              groupId: this.currentGroupId(),
            },
            undefined,
            this.mediaContext()
          )
          .pipe(
            switchMap(
              (response): Observable<MediaCreateResponse & { duration?: number }> => {
                const type = this.getTypeFromMimeType(file.file.type);

                const maybeWithDuration$ =
                  type === 'audio'
                    ? from(this.getDurationFromAudio(file.file)).pipe(
                        map((duration) => ({ ...response, duration }))
                      )
                    : of(response);

                return maybeWithDuration$;
              }
            ),
            switchMap((response) => {
              const type = this.getTypeFromMimeType(file.file.type);
              const maybeCompressed$ =
                type === 'image'
                  ? this._imageCompression.compress(file.file)
                  : of(file.file);

              return maybeCompressed$.pipe(
                switchMap((file) =>
                  this.makeFileUploadRequest(file, {
                    fileId: response.fileId,
                    mediaId: response.mediaId,
                    duration: response.duration,
                  })
                ),
                tap((event) => {
                  switch (event.type) {
                    case HttpEventType.UploadProgress:
                      if (event.total) {
                        file.progress.setMode('determinate');
                        file.progress.setTotal(event.total);
                      }

                      setTimeout(() => {
                        file.progress.setProgress(event.loaded);
                      }, 50);

                      break;

                    case HttpEventType.Response:
                      file.remove();

                      break;
                  }
                }),
                catchError(() => {
                  file.setState({ error: true });

                  return this._media
                    .delete(response.mediaId, undefined, this.mediaContext())
                    .pipe(catchError(() => EMPTY));
                })
              );
            }),
            catchError(() => {
              file.setState({ error: true });

              return EMPTY;
            }),
            finalize(() => {
              file.progress.setMode('indeterminate');
              file.progress.setProgress(0);
              file.progress.setTotal(0);

              file.setState({
                uploading: false,
              });
            })
          );
      }, concurrent),

      finalize(() => this._fetch$.next(FetchState.MEDIA))
    );
  }

  importFromLibrary() {
    const ctx = this.mediaContext();
    if (ctx.scope !== 'client') return;

    this._mediaPicker.open(
      ((selected: MediaItem[]) => {
        return from(selected).pipe(
          mergeMap(
            (item) =>
              this._media
                .import(item.id, ctx, this.currentGroupId())
                .pipe(catchError(() => EMPTY)),
            5
          ),
          toArray(),
          tap(() => this._fetch$.next(FetchState.MEDIA))
        );
      }) as any,
      {
        type: ['audio', 'image'],
        multi: true,
        context: { scope: 'unit' },
      }
    );
  }

  importFromSystem() {
    this._mediaPicker.open(
      ((selected: MediaItem[]) => {
        this._fetch$.next(FetchState.MEDIA);
        return of(selected);
      }) as any,
      {
        type: ['image'],
        multi: true,
        context: this.mediaContext(),
        targetGroupId: this.currentGroupId(),
        initialViewMode: 'system',
      }
    );
  }

  editMedia() {
    const { selected } = this.selection;

    if (selected.length !== 1) {
      return;
    }

    const { id, label } = selected[0];

    const dotIndex = label.lastIndexOf('.');
    const hasExtension = dotIndex > 0;
    const filename = hasExtension ? label.slice(0, dotIndex) : label;
    const extension = hasExtension ? label.slice(dotIndex) : '';

    this._dialog.prompt({
      title: 'Media label bewerken',
      message: '',
      confirmText: 'Bewerken',
      cancelText: 'Annuleren',
      input: {
        mode: 'input',
        defaultValue: filename,
        placeholder: 'Media label',
        validators: {
          required: {
            validator: Validators.required,
            errorMessage: 'Vul een media label in',
          },
          maxlength: {
            validator: Validators.maxLength(255),
            errorMessage: (args) => {
              const errors = args as ValidationErrors;

              return `Maximaal ${errors['requiredLength']} karakters`;
            },
          },
        },
      },
      onConfirm: (label) =>
        of(label).pipe(
          filter((label) => !isNil(label)),
          map((label) => `${label}${extension}`),
          switchMap((label) =>
            this._media.update(id, { label }, undefined, this.mediaContext()).pipe(
              tap(() => {
                this.dataSource.update((mediaData) =>
                  mediaData.map((media) =>
                    media.id === id ? { ...media, label } : media
                  )
                );

                this.selection.clear();
              })
            )
          ),
          catchError(() => {
            const dialog = {
              title: 'Foutmelding',
              message:
                'Er gaat iets fout met het bewerken van de media. Probeer het later nog eens.',
              confirmText: 'Begrepen',
            };

            this._dialog.alert(dialog);

            return of(undefined);
          }),
          switchMap(() => of(undefined))
        ),
    });
  }

  openMediaSettings(): void {
    const { selected } = this.selection;

    if (selected.length === 0) {
      return;
    }

    const dialogData =
      selected.length === 1 ? { media: selected[0] } : { mediaItems: selected };

    const ref = this._dialog.open(MediaSettingsDialogComponent, dialogData);

    ref
      .afterClosed()
      .pipe(
        filter((result): result is { shared: boolean } => !!result),
        tap(() => {
          this.selection.clear();
          this._fetch$.next(FetchState.MEDIA);
        }),
        switchMap(() => this._share.getSharedGroupsTree())
      )
      .subscribe((sharedGroups) => {
        this._sidebarSharedGroups.set(sharedGroups);
        this.sharedGroupList.set(sharedGroups.filter((g) => g.parentId === null));
      });
  }

  shareFolderMedia(group: Group): void {
    const isCurrentlyShared = !!group.shareId;

    if (isCurrentlyShared) {
      // Unshare the folder
      const confirmRef = this._dialog.confirm({
        title: 'Folder niet meer delen?',
        message:
          'Alle media in deze folder (en subfolders) worden niet meer gedeeld.',
        confirmText: 'Niet meer delen',
        cancelText: 'Annuleren',
        onConfirm: () =>
          this._share.unshareGroup(group._id).pipe(
            tap(() => {
              this.groupList.update((groups) =>
                groups.map((g) => (g._id === group._id ? { ...g, shareId: null } : g))
              );
              this._fetch$.next(FetchState.ALL);
            }),
            switchMap(() => this._share.getSharedGroupsTree()),
            tap((sharedGroups) => {
              this._sidebarSharedGroups.set(sharedGroups);
              this.sharedGroupList.set(sharedGroups.filter((g) => g.parentId === null));
            }),
            catchError((error) => {
              confirmRef.close();

              if (isHttpErrorResponse(error)) {
                const metadata = error.error?.detail?.metadata;
                const errorCode = error.error?.detail?.errorCode;

                if (
                  errorCode === 'MEDIA_CROSS_UNIT_REFERENCES_FOUND' &&
                  metadata?.['attachedResources']
                ) {
                  const groups = metadata['attachedResources'] as ReferenceGroup[];

                  // Merge groups that share the same unitId
                  const merged = new Map<string | null, ReferenceGroup>();

                  for (const group of groups) {
                    const existing = merged.get(group.unitId);

                    if (existing) {
                      const seenKeys = new Set(
                        existing.references.map(
                          (r: MediaReference) => `${r.path}::${r.filename}`
                        )
                      );

                      for (const ref of group.references) {
                        const key = `${ref.path}::${ref.filename}`;
                        if (!seenKeys.has(key)) {
                          existing.references.push(ref);
                          seenKeys.add(key);
                        }
                      }

                      existing.totalCount = existing.references.length;
                    } else {
                      merged.set(group.unitId, { ...group });
                    }
                  }

                  const referenceGroups = Array.from(merged.values());

                  this._dialog.open(MediaReferencesDialogComponent, {
                    referenceGroups,
                    mode: 'unshare' as const,
                  });
                  return EMPTY;
                }

                if (
                  errorCode === 'MEDIA_USED_IN_SHARED_TEMPLATE' &&
                  metadata?.['references']
                ) {
                  const templates = metadata['references'] as {
                    label: string;
                  }[];
                  const names = templates.map((t) => t.label).join(', ');
                  this._dialog.alert({
                    title: 'Kan niet stoppen met delen',
                    message: `Media in deze folder wordt gebruikt in gedeelde template(s): ${escapeHtml(names)}. Verwijder eerst de referenties.`,
                    confirmText: 'Begrepen',
                  });
                  return EMPTY;
                }
              }

              this._dialog.alert({
                title: 'Foutmelding',
                message:
                  'Er is iets misgegaan bij het stoppen met delen van de folder. Probeer het later nog eens.',
                confirmText: 'Begrepen',
              });
              return EMPTY;
            })
          ),
      });
    } else {
      // Share the folder
      this._share
        .shareGroup(group._id)
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe({
          next: () => {
            this.groupList.update((groups) =>
              groups.map((g) => (g._id === group._id ? { ...g, shareId: 'shared' } : g))
            );
            this._fetch$.next(FetchState.ALL);
            this._share
              .getSharedGroupsTree()
              .pipe(takeUntilDestroyed(this._destroyRef))
              .subscribe((sharedGroups) => {
                this._sidebarSharedGroups.set(sharedGroups);
                this.sharedGroupList.set(
                  sharedGroups.filter((g) => g.parentId === null)
                );
              });
          },
          error: () => {
            this._dialog.alert({
              title: 'Foutmelding',
              message:
                'Er is iets misgegaan bij het delen van de folder. Probeer het later nog eens.',
              confirmText: 'Begrepen',
            });
          },
        });
    }
  }

  navigateToShared(): void {
    this.selection.clear();

    this._router.navigate(['media'], {
      queryParams: { view: 'shared' },
    });
  }

  navigateToSharedFolder(groupId: string): void {
    this.selection.clear();

    this._router.navigate(['media'], {
      queryParams: { view: 'shared', groupId },
    });
  }

  toggleSharedExpand(nodeId: string, event: Event): void {
    event.stopPropagation();
    const expanded = new Set(this.sharedExpandedIds());
    if (expanded.has(nodeId)) {
      expanded.delete(nodeId);
    } else {
      expanded.add(nodeId);
    }
    this.sharedExpandedIds.set(expanded);
  }

  isSharedNodeExpanded(nodeId: string): boolean {
    return this.sharedExpandedIds().has(nodeId);
  }

  deleteMedia() {
    const mediaItems = this.selection.selected;

    if (!mediaItems.length) {
      return;
    }

    this._dialog.confirm(
      {
        title: 'Media verwijderen?',
        message: `Weet je zeker dat je <strong>${mediaItems.length}</strong> media ${mediaItems.length > 1 ? 'bestanden' : 'bestand'} wilt verwijderen?`,
        confirmText: 'Verwijderen',
        cancelText: 'Annuleren',
        onConfirm: () =>
          from(mediaItems).pipe(
            mergeMap((media) =>
              this._media.delete(media.id, undefined, this.mediaContext()).pipe(
                tap(() =>
                  this.dataSource.update((dataSource) =>
                    dataSource.filter((source) => source.id !== media.id)
                  )
                ),
                map((): StatusResult<MediaItem> => ({ data: media, ok: true })),
                catchError((error) =>
                  of<StatusResult<MediaItem>>({ data: media, ok: false, error })
                )
              )
            ),
            toArray(),
            tap((results) => {
              this.selection.clear();

              const failed = results.filter((result) => !result.ok);

              if (failed.length) {
                const mediaData: MediaAttachedResources[] = [];
                const crossUnitRefs: MediaReference[] = [];

                failed.forEach((result) => {
                  if (isHttpErrorResponse(result.error)) {
                    const metadata = result.error.error?.detail?.metadata;
                    const errorCode = result.error.error?.detail?.errorCode;

                    if (
                      errorCode === 'MEDIA_CROSS_UNIT_REFERENCES_FOUND' &&
                      metadata?.['attachedResources']
                    ) {
                      crossUnitRefs.push(
                        ...(metadata['attachedResources'] as MediaReference[])
                      );
                    } else if (isAttachedResourcesResponse(metadata)) {
                      mediaData.push(...metadata.attachedResources);
                    }
                  }
                });

                if (crossUnitRefs.length) {
                  this._dialog.open(MediaReferencesDialogComponent, {
                    referenceGroups: crossUnitRefs,
                    mode: 'delete' as const,
                  });
                } else if (mediaData.length) {
                  const rawGroups = (mediaData as any[]).map((r) => ({
                    unitId: r.unitId ?? null,
                    unitLabel: r.unitLabel ?? '',
                    references: (r.references ?? [r]).map((ref: any) => ({
                      ...ref,
                      unitId: ref.unitId ?? null,
                      unitLabel: ref.unitLabel ?? '',
                    })),
                    totalCount: r.totalCount ?? 1,
                  }));

                  // Merge groups that share the same unitId
                  const merged = new Map<string | null, (typeof rawGroups)[number]>();

                  for (const group of rawGroups) {
                    const existing = merged.get(group.unitId);

                    if (existing) {
                      const seenPaths = new Set(
                        existing.references.map((r: MediaReference) => r.path)
                      );

                      for (const ref of group.references) {
                        if (!seenPaths.has(ref.path)) {
                          existing.references.push(ref);
                          seenPaths.add(ref.path);
                        }
                      }

                      existing.totalCount += group.totalCount;
                    } else {
                      merged.set(group.unitId, { ...group });
                    }
                  }

                  const referenceGroups = Array.from(merged.values());

                  this._dialog.open(
                    MediaReferencesDialogComponent,
                    {
                      referenceGroups,
                      mode: 'delete' as const,
                    },
                    {
                      disableClose: true,
                    }
                  );
                } else {
                  this._dialog.alert({
                    title: 'Foutmelding',
                    message:
                      'Er is iets fout gegaan met het verwijderen van de media bestanden. Probeer het later nog eens.',
                    confirmText: 'Begrepen',
                  });
                }
              }
            })
          ),
      },
      {
        disableClose: true,
      }
    );
  }

  handleUploadFile() {
    this._fileUploadDialog.open((result) => this.uploadFiles(result!), {
      allowedCategories: ['image', 'audio'],
      allowedExtensions: ['.jpg', '.jpeg', '.png', '.mp3'],
    });
  }

  mapToTreeNode(value: Group): TreeNode {
    return {
      id: value._id,
      parentId: value.parentId,
      name: value.label,
      children: [],
    };
  }

  getTypeFromMimeType(mimeType: string) {
    const parts = mimeType.split('/');

    return parts.length > 1 ? parts[0] : null;
  }

  getMediaFileIcon(mimeType?: string) {
    if (!mimeType) {
      return 'draft';
    }

    const type = this.getTypeFromMimeType(mimeType);

    switch (type) {
      case 'audio':
        return 'music_note';

      case 'image':
        return 'image';

      case 'video':
        return 'movie';

      default:
        return 'draft';
    }
  }

  mapToMediaItem(value: MediaResponse): MediaItem {
    const mediaIcon = this.getMediaFileIcon(value.metadata.mimeType);
    const type = this.getTypeFromMimeType(value.metadata.mimeType);

    return {
      id: value._id,
      label: value.label,
      fileId: value.fileId,
      fileUrl: this._file.get(value.fileId).href,
      icon: mediaIcon,
      type,
      shareId: value.shareId,
      scope: this.mediaContext().scope,
    };
  }

  mapToSharedMediaItem(value: SharedMediaResponse): SharedMediaItem {
    return {
      ...this.mapToMediaItem(value),
      ownerUnitId: value.ownerUnitId,
      ownerUnitLabel: value.ownerUnitLabel,
      isOwn: value.isOwn,
    };
  }

  buildTree(nodes: Group[]) {
    const treeNodes = buildTreeFrom(nodes, this.mapToTreeNode, (a, b) =>
      a.name.localeCompare(b.name)
    );

    const virtualRootNode = this.mapToTreeNode(VIRTUAL_ROOT_NODE);

    virtualRootNode.children = treeNodes;

    return [virtualRootNode];
  }

  buildBreadcrumbs(
    currentId: string,
    items: Group[],
    virtualNode: Group = VIRTUAL_ROOT_NODE
  ) {
    const lookup = new Map(items.map((item) => [item._id, item]));

    const root = {
      id: VIRTUAL_ROOT_NODE._id,
      name: VIRTUAL_ROOT_NODE.label,
    };

    if (currentId === virtualNode._id) {
      return [root];
    }

    const trail: Breadcrumb[] = [];

    let current = lookup.get(currentId);

    while (current) {
      trail.unshift({
        id: current._id,
        name: current.label,
      });

      current = current.parentId ? lookup.get(current.parentId) : undefined;
    }

    trail.unshift(root);

    return trail;
  }

  isPinned(folderId: string): boolean {
    return this.pinnedFolderIds().has(folderId);
  }

  togglePin(folderId: string, event?: Event): void {
    event?.stopPropagation();

    const pins = new Set(this.pinnedFolderIds());
    if (pins.has(folderId)) {
      pins.delete(folderId);
    } else {
      pins.add(folderId);
    }
    this.pinnedFolderIds.set(pins);
    this.savePinnedFolders();
  }

  toggleSidebar(): void {
    const collapsed = !this.sidebarCollapsed();
    this.sidebarCollapsed.set(collapsed);
    try {
      localStorage.setItem('media_sidebar_collapsed', JSON.stringify(collapsed));
    } catch (error) {
      console.error('Failed to save sidebar state:', error);
    }
  }

  private _loadSidebarCollapsed(): boolean {
    try {
      const stored = localStorage.getItem('media_sidebar_collapsed');
      return stored ? JSON.parse(stored) === true : false;
    } catch {
      return false;
    }
  }

  private getStorageKey(): string {
    const unitId = this.currentUnit()?._id ?? 'default';
    const ctx = this.mediaContext();
    const suffix = ctx.scope === 'client' ? `_client_${ctx.clientId}` : '';

    return `media_pinned_folders_${unitId}${suffix}`;
  }

  private loadPinnedFolders(): void {
    try {
      const stored = localStorage.getItem(this.getStorageKey());
      if (stored) {
        const ids = JSON.parse(stored) as string[];
        this.pinnedFolderIds.set(new Set(ids));
      }
    } catch (error) {
      console.error('Failed to load pinned folders:', error);
      this.pinnedFolderIds.set(new Set());
    }
  }

  private savePinnedFolders(): void {
    try {
      const ids = Array.from(this.pinnedFolderIds());
      localStorage.setItem(this.getStorageKey(), JSON.stringify(ids));
    } catch (error) {
      console.error('Failed to save pinned folders:', error);
    }
  }

  private _isSelectable(item: MediaItem): boolean {
    if (!this.canEditMedia()) return false;
    return !(this.isSharedView() && 'isOwn' in item && !item.isOwn);
  }

  private cleanupDeletedPins(): void {
    const currentGroupIds = new Set(this.groupList().map((g) => g._id));
    const pins = this.pinnedFolderIds();
    const validPins = new Set(Array.from(pins).filter((id) => currentGroupIds.has(id)));

    if (validPins.size !== pins.size) {
      this.pinnedFolderIds.set(validPins);
      this.savePinnedFolders();
    }
  }

  private _handleDialogPrompt(
    callback: ConfirmHandler<string>,
    options: { title: string; value?: string }
  ) {
    return this._dialog.prompt(
      {
        title: options.title,
        message: '',
        confirmText: 'Opslaan',
        cancelText: 'Annuleren',
        onConfirm: callback,
        input: {
          mode: 'input',
          placeholder: 'Naam folder',
          defaultValue: options.value,
          validators: {
            required: {
              validator: Validators.required,
              errorMessage: 'Vul een naam in',
            },
            pattern: {
              validator: Validators.pattern(/^[^\\/:*?"<>|]*$/),
              errorMessage:
                'De volgende tekens zijn niet toegestaan: \\ / : * ? " < > |',
            },
            maxlength: {
              validator: Validators.maxLength(100),
              errorMessage: (args) => {
                const errors = args as ValidationErrors;

                return `Maximaal ${errors['requiredLength']} karakters`;
              },
            },
          },
        },
      },
      {
        maxWidth: '396px',
        width: '100%',
      }
    );
  }

  private _getErrorAlert(
    error: unknown,
    defaultMessage: string,
    errorMap: Record<string, { title: string; message: string }> = {}
  ): { title: string; message: string } {
    if (isHttpErrorResponse(error)) {
      const errorCode = error.error?.detail?.errorCode;
      if (errorCode && errorMap[errorCode]) {
        return errorMap[errorCode];
      }
    }
    return { title: 'Foutmelding', message: defaultMessage };
  }
}
